import React, { useState } from "react";
import { AlertTriangle, CheckCircle2, ShieldAlert, XCircle } from "lucide-react";
import { useAdminSanctionsMatches } from "../../hooks/useAdmin";
import { PERMISSIONS, usePermissions } from "../../hooks/usePermissions";
import { ConfirmActionModal } from "./ConfirmActionModal";
import { Pagination } from "./Pagination";
import { readApiError } from "../../utils/readApiError";

const PAGE_SIZE = 25;

const DISPOSITIONS = [
  { value: "pending", label: "Pending" },
  { value: "false_positive", label: "Cleared" },
  { value: "confirmed", label: "Confirmed" },
];

export function AdminSanctionsReview() {
  const [disposition, setDisposition] = useState("pending");
  const [offset, setOffset] = useState(0);
  const [pendingReview, setPendingReview] = useState(null);
  const [actionError, setActionError] = useState(null);
  const { can } = usePermissions();
  const canDecide = can(PERMISSIONS.COMPLIANCE_DECIDE);

  const {
    matches,
    total,
    isLoading,
    error,
    reviewSanctionsMatch,
    isReviewingSanctionsMatch,
  } = useAdminSanctionsMatches({
    limit: PAGE_SIZE,
    offset,
    disposition,
  });

  function changeDisposition(nextDisposition) {
    setDisposition(nextDisposition);
    setOffset(0);
    setActionError(null);
  }

  async function confirmReview(reason) {
    if (!pendingReview) return;
    setActionError(null);
    try {
      await reviewSanctionsMatch({
        matchId: pendingReview.match.id,
        disposition: pendingReview.disposition,
        reason,
      });
      setPendingReview(null);
    } catch (err) {
      setActionError(readApiError(err, "Could not save the sanctions review decision."));
    }
  }

  return (
    <section className="bg-white rounded-3xl p-6 border border-slate-100 shadow-[0_4px_20px_-5px_rgba(0,0,0,0.05)] space-y-5">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <ShieldAlert size={18} className="text-amber-600" />
            <h2 className="text-lg font-serif font-semibold text-[#0A4730]">
              Sanctions screening
            </h2>
          </div>
          <p className="text-xs text-[#0A2540]/50 mt-1 max-w-2xl">
            Review potential UN and UK sanctions matches. Every resolution is recorded
            against the reviewer and retained in the audit trail.
          </p>
        </div>

        <div className="inline-flex items-center gap-1 bg-[#FDFDFD] p-1 rounded-2xl border border-slate-200 self-start">
          {DISPOSITIONS.map((item) => (
            <button
              key={item.value}
              type="button"
              onClick={() => changeDisposition(item.value)}
              className={`px-4 py-2 rounded-xl text-xs font-semibold transition ${
                disposition === item.value
                  ? "bg-[#0A4730] text-white"
                  : "text-[#0A2540]/70 hover:bg-slate-50"
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {(error || (actionError && !pendingReview)) && (
        <div className="rounded-2xl bg-red-50 border border-red-100 p-4 text-sm text-red-600">
          {actionError || "Could not load sanctions screening matches."}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-slate-100">
              <Header>Screened person</Header>
              <Header>Sanctions entry</Header>
              <Header>Source</Header>
              <Header>Match</Header>
              <Header>Status</Header>
              <Header align="right">Review</Header>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isLoading ? (
              <EmptyRow text="Loading sanctions matches…" />
            ) : matches.length === 0 ? (
              <EmptyRow
                text={
                  disposition === "pending"
                    ? "No pending sanctions matches require review."
                    : `No ${labelForDisposition(disposition).toLowerCase()} sanctions matches.`
                }
              />
            ) : (
              matches.map((match) => (
                <tr key={match.id} className="hover:bg-[#F5F9F4]/50 transition-colors">
                  <td className="py-4 pr-4 align-top">
                    <p className="font-medium text-[#0A2540]">{match.screened_name}</p>
                    <p className="text-[11px] text-slate-400 mt-0.5">
                      {humanize(match.subject_type)} · {shortId(match.subject_id)}
                    </p>
                    <div className="mt-2 grid gap-0.5 text-[11px] text-[#0A2540]/60">
                      {match.screened_date_of_birth && (
                        <span>DOB: {match.screened_date_of_birth}</span>
                      )}
                      {(match.screened_nationality || match.screened_country) && (
                        <span>
                          {match.screened_nationality || "—"}
                          {match.screened_country
                            ? ` · ${match.screened_country}`
                            : ""}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-4 pr-4 align-top">
                    <p className="font-medium text-[#0A2540]">{match.listed_name}</p>
                    <p className="text-[11px] text-slate-400 mt-0.5 font-mono">
                      {match.external_reference}
                    </p>
                    <p className="text-[11px] text-[#0A2540]/60 mt-2">
                      Matched as: {match.matched_name}
                    </p>
                  </td>
                  <td className="py-4 pr-4 align-top">
                    <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-semibold text-[#0A2540] uppercase">
                      {match.source}
                    </span>
                  </td>
                  <td className="py-4 pr-4 align-top min-w-40">
                    <Score label="Overall" value={match.overall_score} />
                    <Score label="Name" value={match.name_score} />
                    <div className="flex flex-wrap gap-1 mt-2">
                      <EvidenceFlag label="DOB" value={match.dob_match} />
                      <EvidenceFlag label="Nationality" value={match.nationality_match} />
                      <EvidenceFlag label="Country" value={match.country_match} />
                      <EvidenceFlag label="Document" value={match.document_match} />
                    </div>
                  </td>
                  <td className="py-4 pr-4 align-top">
                    <DispositionBadge value={match.disposition} />
                    <p className="text-[11px] text-slate-400 mt-2">
                      Screened {formatDate(match.screened_at)}
                    </p>
                    {match.reviewed_at && (
                      <p className="text-[11px] text-slate-400 mt-1">
                        Reviewed {formatDate(match.reviewed_at)}
                      </p>
                    )}
                    {match.review_notes && (
                      <p className="text-xs text-[#0A2540]/60 mt-2 max-w-56">
                        {match.review_notes}
                      </p>
                    )}
                  </td>
                  <td className="py-4 text-right align-top">
                    {match.disposition === "pending" ? (
                      canDecide ? (
                        <div className="inline-flex flex-col sm:flex-row gap-2">
                          <button
                            type="button"
                            onClick={() =>
                              setPendingReview({
                                match,
                                disposition: "false_positive",
                              })
                            }
                            className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl border border-emerald-200 text-emerald-700 text-xs font-semibold hover:bg-emerald-50 transition"
                          >
                            <CheckCircle2 size={14} /> Clear false positive
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setPendingReview({ match, disposition: "confirmed" })
                            }
                            className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl border border-red-200 text-red-600 text-xs font-semibold hover:bg-red-50 transition"
                          >
                            <XCircle size={14} /> Confirm match
                          </button>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">Read-only</span>
                      )
                    ) : (
                      <span className="text-xs text-slate-400">Resolved</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        total={total}
        limit={PAGE_SIZE}
        offset={offset}
        onChange={setOffset}
        isLoading={isLoading}
      />

      <ConfirmActionModal
        open={Boolean(pendingReview)}
        title={
          pendingReview?.disposition === "confirmed"
            ? `Confirm sanctions match for ${pendingReview.match.screened_name}?`
            : `Clear ${pendingReview?.match.screened_name || "this person"} as a false positive?`
        }
        description={
          pendingReview?.disposition === "confirmed"
            ? "This records a confirmed sanctions match. Future screening for the same subject and sanctions entry will remain blocked."
            : "This records a subject-specific false-positive decision so the same person and sanctions entry are not returned to pending review on every retry."
        }
        confirmLabel={
          pendingReview?.disposition === "confirmed"
            ? "Confirm match"
            : "Clear false positive"
        }
        destructive={pendingReview?.disposition === "confirmed"}
        requireReason
        reasonLabel="Review rationale"
        reasonPlaceholder="Record the evidence and rationale for this sanctions decision."
        error={actionError}
        isSubmitting={isReviewingSanctionsMatch}
        onConfirm={confirmReview}
        onCancel={() => {
          setPendingReview(null);
          setActionError(null);
        }}
      />
    </section>
  );
}

function Header({ children, align = "left" }) {
  return (
    <th
      className={`pb-4 text-[9px] font-semibold uppercase tracking-[0.2em] text-[#0A2540]/40 ${
        align === "right" ? "text-right" : ""
      }`}
    >
      {children}
    </th>
  );
}

function EmptyRow({ text }) {
  return (
    <tr>
      <td colSpan="6" className="py-10 text-center text-sm text-[#0A2540]/50">
        {text}
      </td>
    </tr>
  );
}

function Score({ label, value }) {
  const numeric = Number(value);
  const formatted = Number.isFinite(numeric) ? `${Math.round(numeric)}%` : "—";
  return (
    <div className="flex items-center justify-between gap-4 text-xs mb-1">
      <span className="text-slate-400">{label}</span>
      <span className="font-semibold text-[#0A2540]">{formatted}</span>
    </div>
  );
}

function EvidenceFlag({ label, value }) {
  if (value == null) return null;
  return (
    <span
      className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${
        value
          ? "border-amber-200 bg-amber-50 text-amber-700"
          : "border-slate-200 bg-slate-50 text-slate-500"
      }`}
    >
      {label}: {value ? "match" : "no"}
    </span>
  );
}

function DispositionBadge({ value }) {
  const config =
    value === "confirmed"
      ? { label: "Confirmed", className: "bg-red-50 text-red-600 border-red-100" }
      : value === "false_positive"
      ? {
          label: "Cleared",
          className: "bg-emerald-50 text-emerald-700 border-emerald-100",
        }
      : { label: "Pending", className: "bg-amber-50 text-amber-700 border-amber-100" };
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${config.className}`}
    >
      {value === "pending" && <AlertTriangle size={13} />}
      {config.label}
    </span>
  );
}

function labelForDisposition(value) {
  return DISPOSITIONS.find((item) => item.value === value)?.label || value;
}

function humanize(value = "") {
  return String(value).replaceAll("_", " ");
}

function shortId(value) {
  const text = String(value || "");
  return text.length > 12 ? `${text.slice(0, 8)}…` : text;
}

function formatDate(value) {
  return value
    ? new Intl.DateTimeFormat(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(value))
    : "—";
}

